/** SUNTRONIX ONE — ONE NETWORK (lifetime relationship & business-card intelligence).
 *  MY ONE → ONE NETWORK. Dashboard, views, business-card scanner (AI extract →
 *  review & confirm), events/trips, follow-up drafting, Contact 360. */
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Users, MapPin, CalendarDays, Plus, ScanLine, UserPlus, Search, Mail, Globe,
  Sparkles, Clock, ChevronRight, Building2, ShieldCheck, Copy, Download, Send,
  UploadCloud, FileSpreadsheet, CheckCircle2, Loader2, Zap, Camera, X,
} from "lucide-react";

const VIEWS = [
  { id: "MY_NETWORK", label: "My Network" },
  { id: "NEW_CONTACTS", label: "New Contacts" },
  { id: "FOLLOW_UP", label: "Follow-up" },
  { id: "EMAIL_READY", label: "Email Ready" },
  { id: "CUSTOMERS", label: "Customers" },
  { id: "VENDORS", label: "Vendors / OEM" },
  { id: "ASSOCIATIONS", label: "Associations" },
  { id: "PERSONAL", label: "Personal" },
];

export default function OneNetwork() {
  const [tab, setTab] = useState("network"); // network | events
  const [view, setView] = useState("MY_NETWORK");
  const [q, setQ] = useState("");
  const [persons, setPersons] = useState([]);
  const [events, setEvents] = useState([]);
  const [dash, setDash] = useState(null);
  const [meta, setMeta] = useState({ relationship_types: [], visibilities: [], email_templates: [] });
  const [createMenu, setCreateMenu] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [fastScanOpen, setFastScanOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [eventOpen, setEventOpen] = useState(false);
  const [batchOpen, setBatchOpen] = useState(false);
  const [quickEmailOpen, setQuickEmailOpen] = useState(false);
  const [metAgainId, setMetAgainId] = useState(null);
  const [personId, setPersonId] = useState(null);
  const [eventId, setEventId] = useState(null);
  const openProfile = (id, sub) => { if (sub === "met-again") setMetAgainId(id); else setPersonId(id); };

  const loadList = useCallback(async () => {
    try {
      const { data } = await api.get(`/one-network/persons?view=${view}&q=${encodeURIComponent(q)}`);
      setPersons(data.items || []);
    } catch (e) { toast.error("Could not load contacts"); }
  }, [view, q]);

  const loadCommon = useCallback(async () => {
    try {
      const [d, ev, m] = await Promise.all([
        api.get("/one-network/dashboard"),
        api.get("/one-network/events"),
        api.get("/one-network/meta"),
      ]);
      setDash(d.data); setEvents(ev.data.items || []); setMeta(m.data);
    } catch (e) { /* non-blocking */ }
  }, []);

  useEffect(() => { loadCommon(); }, [loadCommon]);
  useEffect(() => { loadList(); }, [loadList]);

  const refresh = () => { loadList(); loadCommon(); };
  const doExport = (fmt) => window.open(`${api.defaults.baseURL}/one-network/export?format=${fmt}`, "_blank");

  return (
    <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-6 space-y-5" data-testid="one-network-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-indigo-600" />
            <h1 className="text-xl font-semibold text-slate-900">ONE Network</h1>
          </div>
          <p className="text-[13px] text-slate-500 mt-0.5">Your lifetime relationship & business-card intelligence.</p>
        </div>
        <div className="relative">
          <Button size="sm" onClick={() => setCreateMenu((v) => !v)} data-testid="network-create-btn"
            className="bg-indigo-600 hover:bg-indigo-700">
            <Plus className="h-4 w-4 mr-1.5" /> Create
          </Button>
          {createMenu && (
            <div className="absolute right-0 mt-1 w-52 bg-white border border-slate-200 rounded-lg shadow-lg z-20 py-1"
              data-testid="network-create-menu">
              {[["Scan Business Card", ScanLine, () => setScanOpen(true), "create-scan-card"],
                ["Fast Scan (Mobile)", Zap, () => setFastScanOpen(true), "create-fast-scan"],
                ["Contact", UserPlus, () => setContactOpen(true), "create-contact"],
                ["Quick Email", Mail, () => setQuickEmailOpen(true), "create-quick-email"],
                ["Batch Add", Users, () => setBatchOpen(true), "create-batch"],
                ["Event / Trip", CalendarDays, () => setEventOpen(true), "create-event"]].map(([l, Ic, fn, tid]) => (
                <button key={tid} data-testid={tid}
                  onClick={() => { setCreateMenu(false); fn(); }}
                  className="flex items-center gap-2 w-full px-3 py-2 text-[13px] text-slate-700 hover:bg-slate-50">
                  <Ic className="h-4 w-4 text-slate-400" /> {l}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Dashboard stats */}
      {dash && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2" data-testid="network-dashboard">
          {[["Contacts", dash.total_contacts, Users], ["Countries", dash.countries, Globe],
            ["Events", dash.events, CalendarDays], ["New this month", dash.new_this_month, Sparkles],
            ["Follow-ups due", dash.follow_ups_due, Clock], ["Emails pending", dash.emails_pending, Mail]].map(([k, v, Ic]) => (
            <div key={k} className="rounded-lg border border-slate-200 bg-white px-3 py-2">
              <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-slate-400"><Ic className="h-3 w-3" />{k}</div>
              <div className="text-lg font-semibold text-slate-900">{Number(v || 0).toLocaleString()}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200">
        {[["network", "My Network"], ["events", "Events & Trips"]].map(([id, l]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`network-tab-${id}`}
            className={`px-3 py-2 text-[13px] font-medium border-b-2 -mb-px ${
              tab === id ? "border-indigo-600 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            {l}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => doExport("csv")} data-testid="network-export-csv"><Download className="h-3.5 w-3.5 mr-1" />CSV</Button>
          <Button variant="ghost" size="sm" onClick={() => doExport("vcard")} data-testid="network-export-vcard">vCard</Button>
        </div>
      </div>

      {tab === "network" && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            {VIEWS.map((v) => (
              <button key={v.id} onClick={() => setView(v.id)} data-testid={`view-${v.id}`}
                className={`h-7 px-2.5 rounded-full text-[12px] border transition-colors ${
                  view === v.id ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>{v.label}</button>
            ))}
            <div className="relative ml-auto">
              <Search className="h-3.5 w-3.5 absolute left-2 top-2.5 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people…"
                className="h-8 pl-7 w-56 text-[13px]" data-testid="network-search" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="network-contact-list">
            {persons.map((p) => (
              <button key={p.id} onClick={() => { setPersonId(p.id); }} data-testid={`contact-card-${p.id}`}
                className="text-left rounded-lg border border-slate-200 bg-white p-3 hover:border-indigo-300 hover:shadow-sm transition-all">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900 text-[14px] truncate flex items-center gap-1">
                      {p.full_name}
                      {p.verified && <ShieldCheck className="h-3.5 w-3.5 text-emerald-500 shrink-0" />}
                    </div>
                    <div className="text-[12px] text-slate-500 truncate">{p.designation}{p.company ? ` · ${p.company}` : ""}</div>
                    <div className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-1">
                      {(p.city || p.country) && <><MapPin className="h-3 w-3" />{[p.city, p.country].filter(Boolean).join(", ")}</>}
                    </div>
                  </div>
                  {p.visibility === "PRIVATE" && <Badge className="text-[9px] border bg-slate-50 text-slate-500 border-slate-200">Private</Badge>}
                </div>
                {(p.specialities || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {p.specialities.slice(0, 3).map((s) => (
                      <span key={s} className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700">{s}</span>
                    ))}
                  </div>
                )}
              </button>
            ))}
            {persons.length === 0 && <div className="text-[13px] text-slate-400 py-8 text-center col-span-full">No contacts in this view yet.</div>}
          </div>
        </div>
      )}

      {tab === "events" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="network-events-list">
          {events.map((e) => (
            <button key={e.id} onClick={() => setEventId(e.id)} data-testid={`event-card-${e.id}`}
              className="text-left rounded-lg border border-slate-200 bg-white p-3 hover:border-indigo-300 transition-all">
              <div className="flex items-center justify-between">
                <div className="font-medium text-slate-900 text-[14px]">{e.name}</div>
                <Badge className="text-[10px] border bg-slate-50 text-slate-600 border-slate-200">{e.event_type}</Badge>
              </div>
              <div className="text-[12px] text-slate-500 mt-0.5">{[e.city, e.country].filter(Boolean).join(", ")}</div>
              <div className="text-[11px] text-slate-400 mt-1">{e.contact_count} contacts met</div>
            </button>
          ))}
          {events.length === 0 && <div className="text-[13px] text-slate-400 py-8 text-center col-span-full">No events yet. Create one to start capturing cards.</div>}
        </div>
      )}

      {scanOpen && <ScanDialog events={events} meta={meta} onClose={() => setScanOpen(false)} onDone={refresh} />}
      {fastScanOpen && <FastScanDialog events={events} meta={meta} onClose={() => setFastScanOpen(false)} onDone={refresh} />}
      {contactOpen && <ContactDialog meta={meta} events={events} onClose={() => setContactOpen(false)} onDone={refresh} onOpenProfile={openProfile} />}
      {batchOpen && <BatchAdd onClose={() => setBatchOpen(false)} onDone={refresh} />}
      {quickEmailOpen && <QuickEmailDialog meta={meta} onClose={() => setQuickEmailOpen(false)} />}
      {eventOpen && <EventDialog onClose={() => setEventOpen(false)} onDone={refresh} />}
      {metAgainId && <MetAgainDialog personId={metAgainId} events={events} onClose={() => setMetAgainId(null)} onDone={refresh} />}
      {personId && <Contact360 personId={personId} onClose={() => setPersonId(null)} onChanged={refresh} onMetAgain={(id) => { setPersonId(null); setMetAgainId(id); }} />}
      {eventId && <EventDetail eventId={eventId} meta={meta} onClose={() => setEventId(null)} onChanged={refresh} />}
    </div>
  );
}

/* ── Scan Business Card ─────────────────────────────────────────── */
const ScanDialog = ({ events, meta, onClose, onDone }) => {
  const [front, setFront] = useState(null);
  const [back, setBack] = useState(null);
  const [evId, setEvId] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [scan, setScan] = useState(null); // {card, duplicate_exact, duplicate_candidates}
  const [fields, setFields] = useState({});
  const [linkId, setLinkId] = useState("");

  const runScan = async () => {
    if (!front) { toast.error("Add the front image of the card"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("front", front);
      if (back) fd.append("back", back);
      if (evId) fd.append("event_id", evId);
      if (note) fd.append("memory_note", note);
      const { data } = await api.post("/one-network/cards/scan", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setScan(data);
      const e = data.card.extracted || {};
      setFields({
        full_name: e.full_name || "", honorific: "", surname: "", designation: e.designation || "", company: e.company || "",
        email: e.email || "", mobile: e.mobile || "", city: e.city || "", country: e.country || "",
        website: e.website || "", specialities: (e.suggested_specialities || []).join(", "),
        relationship_types: "", visibility: "PRIVATE",
      });
      if (data.duplicate_exact) { setLinkId(data.duplicate_exact.id); toast.info("Possible existing contact detected — review below"); }
      toast.success("Card read — review & confirm the details");
    } catch (e) { toast.error(e.response?.data?.detail || "Scan failed"); }
    setBusy(false);
  };

  const confirm = async () => {
    if (!fields.full_name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      await api.post(`/one-network/cards/${scan.card.id}/verify`, {
        fields: {
          ...fields,
          specialities: fields.specialities ? fields.specialities.split(",").map((s) => s.trim()).filter(Boolean) : [],
          relationship_types: fields.relationship_types ? [fields.relationship_types] : [],
        },
        link_person_id: linkId || null,
        event_id: evId || null,
        memory_note: note || null,
      });
      toast.success("Contact verified & saved");
      onDone(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[86vh] overflow-y-auto" data-testid="scan-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2"><ScanLine className="h-4 w-4" /> Scan Business Card</DialogTitle>
          <DialogDescription className="text-[12px]">Upload the card, let ONE read it, then review & confirm. The original image is kept.</DialogDescription>
        </DialogHeader>

        {!scan ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              {[["Front (required)", front, setFront, "scan-front"], ["Back (optional)", back, setBack, "scan-back"]].map(([l, val, set, tid]) => (
                <label key={tid} className="border-2 border-dashed border-slate-200 rounded-lg p-3 text-center cursor-pointer hover:border-indigo-300 block">
                  <input type="file" accept="image/*" className="hidden" data-testid={tid}
                    onChange={(e) => set(e.target.files?.[0] || null)} />
                  {val ? <img src={URL.createObjectURL(val)} alt="card" className="max-h-28 mx-auto rounded" />
                    : <div className="text-[12px] text-slate-400 py-6">{l}</div>}
                </label>
              ))}
            </div>
            <select value={evId} onChange={(e) => setEvId(e.target.value)} data-testid="scan-event"
              className="w-full h-9 border border-slate-200 rounded-md px-2 text-[13px] bg-white">
              <option value="">— Link to event / trip (optional) —</option>
              {events.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="scan-note"
              placeholder="What should ONE remember? (e.g. interested in device lifecycle, met through Kumar)" />
            <DialogFooter>
              <Button onClick={runScan} disabled={busy} data-testid="scan-run" className="bg-indigo-600 hover:bg-indigo-700">
                {busy ? "Reading…" : "Read Card"}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3" data-testid="scan-review">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="border border-slate-200 rounded-lg p-2 bg-slate-50">
                {front && <img src={URL.createObjectURL(front)} alt="card" className="w-full rounded" />}
              </div>
              <div className="space-y-2">
                {scan.duplicate_exact && (
                  <div className="text-[12px] rounded bg-amber-50 border border-amber-200 px-2 py-1.5 text-amber-800" data-testid="scan-dup-exact">
                    Existing contact found: <b>{scan.duplicate_exact.full_name}</b> — this card will be added as a new affiliation.
                  </div>
                )}
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-slate-400">Honorific / Salutation (select — never guessed)</div>
                  <select value={fields.honorific || ""} onChange={(e) => setFields({ ...fields, honorific: e.target.value })} data-testid="review-honorific"
                    className="w-full h-8 border border-slate-200 rounded-md px-2 text-[13px] bg-white">
                    <option value="">— select —</option>
                    {(meta.honorifics || []).map((h) => <option key={h} value={h}>{h}</option>)}
                  </select>
                </div>
                {[["full_name", "Full name"], ["designation", "Designation"], ["company", "Company"],
                  ["email", "Email"], ["mobile", "Mobile"], ["city", "City"], ["country", "Country"],
                  ["specialities", "Specialities (comma)"]].map(([k, l]) => (
                  <div key={k}>
                    <div className="text-[10px] uppercase tracking-wide text-slate-400">{l}</div>
                    <Input value={fields[k] || ""} onChange={(e) => setFields({ ...fields, [k]: e.target.value })}
                      className="h-8 text-[13px]" data-testid={`review-${k}`} />
                  </div>
                ))}
                <div className="grid grid-cols-2 gap-2">
                  <select value={fields.relationship_types} data-testid="review-relationship"
                    onChange={(e) => setFields({ ...fields, relationship_types: e.target.value })}
                    className="h-8 border border-slate-200 rounded-md px-2 text-[12px] bg-white">
                    <option value="">Relationship…</option>
                    {meta.relationship_types.map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
                  </select>
                  <select value={fields.visibility} data-testid="review-visibility"
                    onChange={(e) => setFields({ ...fields, visibility: e.target.value })}
                    className="h-8 border border-slate-200 rounded-md px-2 text-[12px] bg-white">
                    {meta.visibilities.map((v) => <option key={v} value={v}>{v.replace(/_/g, " ")}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => setScan(null)}>Back</Button>
              <Button onClick={confirm} disabled={busy} data-testid="scan-confirm" className="bg-emerald-600 hover:bg-emerald-700">
                Confirm & Save Contact
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

/* ── Mobile Fast-Scan (batch capture → bulk verify) ──────────────── */
const FastScanDialog = ({ events, meta, onClose, onDone }) => {
  const [evId, setEvId] = useState("");
  const [queue, setQueue] = useState([]); // [{id, file, url}]
  const [phase, setPhase] = useState("capture"); // capture | scanning | review | done
  const [rows, setRows] = useState([]); // [{card_id, front_url, fields, dup, skip}]
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const addFiles = (fileList) => {
    const files = Array.from(fileList || []);
    setQueue((q) => [
      ...q,
      ...files.map((f) => ({ id: `${f.name}-${f.size}-${Math.random().toString(36).slice(2, 7)}`, file: f, url: URL.createObjectURL(f) })),
    ]);
  };
  const removeQ = (id) => setQueue((q) => q.filter((x) => x.id !== id));

  const scanAll = async () => {
    if (queue.length === 0) { toast.error("Snap or add at least one card"); return; }
    setBusy(true); setPhase("scanning");
    try {
      const fd = new FormData();
      queue.forEach((q) => fd.append("files", q.file));
      if (evId) fd.append("event_id", evId);
      const { data } = await api.post("/one-network/cards/fast-scan", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const mapped = (data.cards || []).map((c, i) => {
        const e = c.extracted || {};
        return {
          card_id: c.card.id,
          front_url: queue[i]?.url,
          dup: c.duplicate_exact || null,
          link_person_id: c.duplicate_exact ? c.duplicate_exact.id : "",
          skip: false,
          fields: {
            full_name: e.full_name || "", honorific: "", designation: e.designation || "",
            company: e.company || "", email: e.email || "", mobile: e.mobile || "",
            city: e.city || "", country: e.country || "",
            specialities: (e.suggested_specialities || []).join(", "),
            relationship_types: "", visibility: "PRIVATE",
          },
        };
      });
      setRows(mapped);
      setPhase("review");
      const named = mapped.filter((r) => r.fields.full_name.trim()).length;
      toast.success(`${mapped.length} card(s) read · ${named} with a name`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fast-scan failed");
      setPhase("capture");
    }
    setBusy(false);
  };

  const patchRow = (i, patch) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const patchField = (i, k, v) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, fields: { ...r.fields, [k]: v } } : r)));

  const saveAll = async () => {
    const toSave = rows.filter((r) => !r.skip && r.fields.full_name.trim());
    if (toSave.length === 0) { toast.error("Nothing to save — add a name or a card is skipped"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/one-network/cards/fast-verify", {
        event_id: evId || null,
        items: rows.map((r) => ({
          card_id: r.card_id,
          skip: r.skip,
          link_person_id: r.link_person_id || null,
          fields: {
            ...r.fields,
            specialities: r.fields.specialities ? r.fields.specialities.split(",").map((s) => s.trim()).filter(Boolean) : [],
            relationship_types: r.fields.relationship_types ? [r.fields.relationship_types] : [],
          },
        })),
      });
      setResult(data);
      setPhase("done");
      toast.success(`${data.saved} contact(s) saved`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save batch"); }
    setBusy(false);
  };

  const namedCount = rows.filter((r) => !r.skip && r.fields.full_name.trim()).length;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] flex flex-col p-0" data-testid="fast-scan-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-100">
          <DialogTitle className="text-base flex items-center gap-2"><Zap className="h-4 w-4 text-amber-500" /> Fast Scan — Batch Card Capture</DialogTitle>
          <DialogDescription className="text-[12px]">
            Snap card after card, then review & save the whole stack in one go. Perfect for events.
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 min-h-0 px-5 py-4 space-y-3">
          {/* CAPTURE */}
          {phase === "capture" && (
            <div className="space-y-3">
              <select value={evId} onChange={(e) => setEvId(e.target.value)} data-testid="fast-scan-event"
                className="w-full h-10 border border-slate-200 rounded-md px-2 text-[13px] bg-white">
                <option value="">— Link this stack to an event / trip (optional) —</option>
                {events.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-3">
                <label className="border-2 border-dashed border-indigo-200 rounded-xl p-4 text-center cursor-pointer hover:border-indigo-400 bg-indigo-50/40 flex flex-col items-center justify-center gap-1.5" data-testid="fast-scan-camera-label">
                  <input type="file" accept="image/*" capture="environment" multiple className="hidden"
                    data-testid="fast-scan-camera" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
                  <Camera className="h-6 w-6 text-indigo-500" />
                  <div className="text-[12px] font-medium text-indigo-700">Take photo</div>
                  <div className="text-[10px] text-slate-400">Uses your camera on mobile</div>
                </label>
                <label className="border-2 border-dashed border-slate-200 rounded-xl p-4 text-center cursor-pointer hover:border-slate-300 flex flex-col items-center justify-center gap-1.5" data-testid="fast-scan-gallery-label">
                  <input type="file" accept="image/*" multiple className="hidden"
                    data-testid="fast-scan-gallery" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
                  <UploadCloud className="h-6 w-6 text-slate-400" />
                  <div className="text-[12px] font-medium text-slate-600">Add from gallery</div>
                  <div className="text-[10px] text-slate-400">Select multiple cards</div>
                </label>
              </div>
              {queue.length > 0 && (
                <div>
                  <div className="text-[11px] text-slate-500 mb-1.5" data-testid="fast-scan-queue-count">{queue.length} card(s) queued</div>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="fast-scan-queue">
                    {queue.map((q) => (
                      <div key={q.id} className="relative group">
                        <img src={q.url} alt="card" className="w-full h-20 object-cover rounded-lg border border-slate-200" />
                        <button onClick={() => removeQ(q.id)} data-testid={`fast-scan-remove-${q.id}`}
                          className="absolute -top-1.5 -right-1.5 bg-white border border-slate-200 rounded-full p-0.5 shadow-sm hover:bg-red-50">
                          <X className="h-3 w-3 text-slate-500" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* SCANNING */}
          {phase === "scanning" && (
            <div className="py-14 text-center" data-testid="fast-scan-progress">
              <Loader2 className="h-8 w-8 text-indigo-500 animate-spin mx-auto mb-3" />
              <div className="text-[13px] text-slate-600">Reading {queue.length} card(s) with ONE…</div>
              <div className="text-[11px] text-slate-400 mt-1">This runs all cards in parallel — a few seconds.</div>
            </div>
          )}

          {/* REVIEW */}
          {phase === "review" && (
            <div className="space-y-2.5" data-testid="fast-scan-review">
              {rows.map((r, i) => (
                <div key={r.card_id} data-testid={`fast-row-${i}`}
                  className={`rounded-xl border p-2.5 flex gap-3 ${r.skip ? "border-slate-200 bg-slate-50 opacity-60" : "border-slate-200 bg-white"}`}>
                  <div className="w-16 shrink-0">
                    {r.front_url && <img src={r.front_url} alt="card" className="w-16 h-20 object-cover rounded-md border border-slate-200" />}
                  </div>
                  <div className="flex-1 min-w-0 space-y-1.5">
                    {r.dup && !r.skip && (
                      <div className="text-[11px] rounded bg-amber-50 border border-amber-200 px-2 py-1 text-amber-800" data-testid={`fast-row-dup-${i}`}>
                        Matches existing <b>{r.dup.full_name}</b> — will add as a new affiliation.
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-1.5">
                      <select value={r.fields.honorific} onChange={(e) => patchField(i, "honorific", e.target.value)}
                        data-testid={`fast-row-honorific-${i}`} className="h-8 border border-slate-200 rounded-md px-2 text-[12px] bg-white">
                        <option value="">Salutation…</option>
                        {(meta.honorifics || []).map((h) => <option key={h} value={h}>{h}</option>)}
                      </select>
                      <Input value={r.fields.full_name} onChange={(e) => patchField(i, "full_name", e.target.value)}
                        placeholder="Full name *" className="h-8 text-[12px]" data-testid={`fast-row-name-${i}`} />
                      <Input value={r.fields.company} onChange={(e) => patchField(i, "company", e.target.value)}
                        placeholder="Company" className="h-8 text-[12px]" data-testid={`fast-row-company-${i}`} />
                      <Input value={r.fields.designation} onChange={(e) => patchField(i, "designation", e.target.value)}
                        placeholder="Designation" className="h-8 text-[12px]" data-testid={`fast-row-designation-${i}`} />
                      <Input value={r.fields.email} onChange={(e) => patchField(i, "email", e.target.value)}
                        placeholder="Email" className="h-8 text-[12px]" data-testid={`fast-row-email-${i}`} />
                      <Input value={r.fields.mobile} onChange={(e) => patchField(i, "mobile", e.target.value)}
                        placeholder="Mobile" className="h-8 text-[12px]" data-testid={`fast-row-mobile-${i}`} />
                    </div>
                  </div>
                  <div className="shrink-0 flex flex-col items-center gap-1">
                    <button onClick={() => patchRow(i, { skip: !r.skip })} data-testid={`fast-row-skip-${i}`}
                      className={`text-[10px] px-2 py-1 rounded border ${r.skip ? "border-indigo-300 text-indigo-600 bg-indigo-50" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
                      {r.skip ? "Include" : "Skip"}
                    </button>
                  </div>
                </div>
              ))}
              {!rows.some((r) => r.fields.full_name.trim()) && (
                <div className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                  No names were read — type a name on the cards you want to keep.
                </div>
              )}
            </div>
          )}

          {/* DONE */}
          {phase === "done" && result && (
            <div className="py-10 text-center" data-testid="fast-scan-done">
              <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto mb-3" />
              <div className="text-[15px] font-semibold text-slate-800">{result.saved} contact(s) saved</div>
              <div className="text-[12px] text-slate-500 mt-1">
                {result.skipped > 0 && <>{result.skipped} skipped · </>}
                {result.errors?.length > 0 ? `${result.errors.length} could not be saved` : "All good."}
              </div>
              {result.errors?.length > 0 && (
                <div className="mt-3 text-left text-[11px] text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1.5 max-w-sm mx-auto" data-testid="fast-scan-errors">
                  {result.errors.map((e, i) => <div key={i}>• {e.error}</div>)}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="px-5 py-3 border-t border-slate-100">
          {phase === "capture" && (
            <Button onClick={scanAll} disabled={busy || queue.length === 0} data-testid="fast-scan-run"
              className="bg-indigo-600 hover:bg-indigo-700">
              <Zap className="h-4 w-4 mr-1.5" /> Read {queue.length || ""} card(s)
            </Button>
          )}
          {phase === "review" && (
            <>
              <Button variant="outline" size="sm" onClick={() => setPhase("capture")}>Add more</Button>
              <Button onClick={saveAll} disabled={busy || namedCount === 0} data-testid="fast-scan-save"
                className="bg-emerald-600 hover:bg-emerald-700">
                {busy ? "Saving…" : `Save ${namedCount} contact(s)`}
              </Button>
            </>
          )}
          {phase === "done" && (
            <Button onClick={onClose} data-testid="fast-scan-close" className="bg-indigo-600 hover:bg-indigo-700">Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ── ADD TO MY NETWORK (manual entry — Quick Add / Full Profile) ─── */
const CHIP = "px-2 py-0.5 rounded-full text-[11px] border cursor-pointer transition-colors";
const MultiChips = ({ options, value, onChange, testid, allowFree }) => {
  const [free, setFree] = useState("");
  const toggle = (o) => onChange(value.includes(o) ? value.filter((x) => x !== o) : [...value, o]);
  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap gap-1">
        {options.map((o) => (
          <span key={o} onClick={() => toggle(o)}
            className={`${CHIP} ${value.includes(o) ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
            {o.replace(/_/g, " ")}
          </span>
        ))}
        {value.filter((v) => !options.includes(v)).map((o) => (
          <span key={o} onClick={() => toggle(o)} className={`${CHIP} bg-indigo-600 text-white border-indigo-600`}>{o} ×</span>
        ))}
      </div>
      {allowFree && (
        <div className="flex gap-1 mt-1">
          <Input value={free} onChange={(e) => setFree(e.target.value)} placeholder="Add other…" className="h-7 text-[12px]" />
          <Button size="sm" variant="outline" className="h-7"
            onClick={() => { if (free.trim()) { onChange([...value, free.trim()]); setFree(""); } }}>Add</Button>
        </div>
      )}
    </div>
  );
};

const SPECIALITIES = ["Enterprise Storage", "Cybersecurity", "Cloud", "Apple", "Microsoft", "Networking",
  "Data Centre", "Mobility", "Finance", "Legal", "Logistics", "Manufacturing", "Hospitality",
  "Government", "Technology Distribution"];
const MEETING_TYPES = ["CONFERENCE", "EXHIBITION", "CLIENT_MEETING", "STORE_VISIT", "ASSOCIATION",
  "CLUB", "BUSINESS_DELEGATION", "SOCIAL", "PERSONAL", "REFERRAL", "OTHER"];

const ContactDialog = ({ meta, events, presetEvent, onClose, onDone, onOpenProfile }) => {
  const [mode, setMode] = useState("QUICK");
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({
    full_name: "", honorific: "", surname: "", salutation_display_name: "",
    preferred_name: "", native_name: "", romanised_name: "",
    company: "", department: "", designation: "", email: "", alt_email: "", mobile: "", alt_mobile: "",
    office_phone: "", website: "", social_links: "", city: "", country: "", postal_address: "",
    relationship_types: [], specialities: [], visibility: "PRIVATE", memory_note: "",
    date_met: today, met_city: "", met_country: "", event_id: presetEvent || "", venue: "",
    meeting_type: "", introduced_by: "", with_me: "",
    discussed: "", opportunity: "", products_interest: "", suntronix_help: "", they_help: "",
    fu_required: false, fu_date: "", fu_priority: "NORMAL", fu_next_action: "",
  });
  const [busy, setBusy] = useState(false);
  const [dupMatch, setDupMatch] = useState(null);
  const [saved, setSaved] = useState(null);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const checkDupe = async () => {
    if (!f.email && !f.mobile && !f.full_name) return;
    try {
      const { data } = await api.post("/one-network/persons/check-duplicate",
        { full_name: f.full_name, company: f.company, email: f.email, mobile: f.mobile });
      setDupMatch(data.exact || null);
    } catch { /* ignore */ }
  };

  const buildBody = (forceNew) => ({
    full_name: f.full_name, honorific: f.honorific === "Other" ? (f.honorific_custom || null) : (f.honorific || null),
    surname: f.surname || null, salutation_display_name: f.salutation_display_name || null,
    preferred_name: f.preferred_name || null, native_name: f.native_name || null,
    romanised_name: f.romanised_name || null, company: f.company, department: f.department || null,
    designation: f.designation || null, email: f.email || null, alt_email: f.alt_email || null,
    mobile: f.mobile || null, alt_mobile: f.alt_mobile || null, office_phone: f.office_phone || null,
    website: f.website || null, city: f.city || null, country: f.country || null,
    postal_address: f.postal_address || null,
    social_links: f.social_links ? f.social_links.split(",").map((s) => s.trim()).filter(Boolean) : [],
    relationship_types: f.relationship_types, specialities: f.specialities, visibility: f.visibility,
    memory_note: f.memory_note || null, force_new: forceNew,
    how_we_met: { date_met: f.date_met, city: f.met_city || f.city, country: f.met_country || f.country,
      event_id: f.event_id || null, venue: f.venue || null, meeting_type: f.meeting_type || null,
      introduced_by: f.introduced_by || null, with_me: f.with_me || null },
    discussion: { discussed: f.discussed || null, opportunity: f.opportunity || null,
      products_interest: f.products_interest || null, suntronix_help: f.suntronix_help || null,
      they_help: f.they_help || null },
    follow_up: { required: f.fu_required, date: f.fu_date || null, next_action: f.fu_next_action || null, priority: f.fu_priority },
  });

  const save = async (prepareFollowup, forceNew) => {
    if (!f.full_name.trim() || !f.company.trim()) { toast.error("Full name and company are required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/one-network/persons", buildBody(forceNew));
      if (data.duplicate) { setDupMatch(data.match); setBusy(false); return; }
      onDone();
      if (prepareFollowup && data.id) {
        try {
          await api.post("/one-network/comms/prepare", { person_id: data.id, event_id: f.event_id || null });
          toast.success("Contact saved & follow-up draft prepared");
        } catch { toast.success("Contact saved (add an email to prepare a follow-up)"); }
      } else {
        toast.success("Contact saved");
      }
      setSaved(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    setBusy(false);
  };

  if (saved) {
    return (
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="sm:max-w-sm" data-testid="contact-saved">
          <DialogHeader><DialogTitle className="text-base flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-500" /> Contact saved</DialogTitle>
            <DialogDescription className="text-[12px]">{saved.full_name} is now in your network.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" size="sm" data-testid="saved-open-profile" onClick={() => { onOpenProfile?.(saved.id); onClose(); }}>Open profile</Button>
            <Button variant="outline" size="sm" data-testid="saved-add-another" onClick={() => { setSaved(null); setDupMatch(null); setF((p) => ({ ...p, full_name: "", company: "", designation: "", email: "", mobile: "", memory_note: "", relationship_types: [], specialities: [] })); }}>Add another</Button>
            <Button variant="outline" size="sm" className="col-span-2" data-testid="saved-close" onClick={onClose}>Done</Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const L = ({ children }) => <div className="text-[10px] uppercase tracking-wide text-slate-400 mt-1">{children}</div>;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="contact-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Add to My Network</DialogTitle>
          <DialogDescription className="text-[12px]">Build a relationship record you can return to for life.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-1">
          {[["QUICK", "Quick Add"], ["FULL", "Full Profile"]].map(([m, l]) => (
            <button key={m} onClick={() => setMode(m)} data-testid={`mode-${m}`}
              className={`h-7 px-3 rounded-md text-[12px] border ${mode === m ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}>{l}</button>
          ))}
        </div>

        {dupMatch && (
          <div className="rounded bg-amber-50 border border-amber-200 px-3 py-2 text-[12px] text-amber-900" data-testid="dup-warning">
            Possible existing contact: <b>{dupMatch.full_name}</b> ({dupMatch.company}).
            <div className="flex gap-2 mt-1">
              <Button size="sm" variant="outline" className="h-7" data-testid="dup-met-again"
                onClick={() => { onOpenProfile?.(dupMatch.id, "met-again"); onClose(); }}>Add "Met Again"</Button>
              <Button size="sm" variant="outline" className="h-7" data-testid="dup-force-new"
                onClick={() => save(false, true)}>Create as new person</Button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-x-3 gap-y-0">
          <div>
            <L>Honorific / Salutation</L>
            <select value={f.honorific} onChange={(e) => set("honorific", e.target.value)} data-testid="cf-honorific"
              className="w-full h-8 border border-slate-200 rounded-md px-2 text-[13px] bg-white">
              <option value="">— select —</option>
              {(meta.honorifics || []).map((h) => <option key={h} value={h}>{h}</option>)}
              <option value="Other">Other / Custom</option>
            </select>
          </div>
          {f.honorific === "Other" && (
            <div><L>Custom honorific</L><Input value={f.honorific_custom || ""} onChange={(e) => set("honorific_custom", e.target.value)} data-testid="cf-honorific-custom" className="h-8 text-[13px]" /></div>
          )}
          <div><L>Full name *</L><Input value={f.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="cf-full_name" className="h-8 text-[13px]" /></div>
          <div><L>Surname / family name</L><Input value={f.surname} onChange={(e) => set("surname", e.target.value)} data-testid="cf-surname" className="h-8 text-[13px]" /></div>
          <div className="col-span-2"><L>Salutation display (e.g. "Mr. Tanaka") — optional override</L><Input value={f.salutation_display_name} onChange={(e) => set("salutation_display_name", e.target.value)} data-testid="cf-salutation-display" className="h-8 text-[13px]" placeholder={f.honorific && f.honorific !== "Other" ? `${f.honorific} ${f.surname || (f.full_name.split(" ").slice(-1)[0] || "")}` : ""} /></div>
          <div><L>Company / Organisation *</L><Input value={f.company} onChange={(e) => set("company", e.target.value)} data-testid="cf-company" className="h-8 text-[13px]" /></div>
          <div><L>Designation</L><Input value={f.designation} onChange={(e) => set("designation", e.target.value)} data-testid="cf-designation" className="h-8 text-[13px]" /></div>
          <div><L>Email</L><Input value={f.email} onBlur={checkDupe} onChange={(e) => set("email", e.target.value)} data-testid="cf-email" className="h-8 text-[13px]" /></div>
          <div><L>Mobile</L><Input value={f.mobile} onBlur={checkDupe} onChange={(e) => set("mobile", e.target.value)} data-testid="cf-mobile" className="h-8 text-[13px]" /></div>
          <div><L>Country *</L><Input value={f.country} onChange={(e) => set("country", e.target.value)} data-testid="cf-country" className="h-8 text-[13px]" /></div>
          <div><L>City</L><Input value={f.city} onChange={(e) => set("city", e.target.value)} data-testid="cf-city" className="h-8 text-[13px]" /></div>
          <div>
            <L>Visibility</L>
            <select value={f.visibility} onChange={(e) => set("visibility", e.target.value)} data-testid="cf-visibility" className="w-full h-8 border border-slate-200 rounded-md px-2 text-[12px] bg-white">
              {meta.visibilities.map((v) => <option key={v} value={v}>{v.replace(/_/g, " ")}</option>)}
            </select>
          </div>
        </div>

        {mode === "FULL" && (
          <div className="grid grid-cols-2 gap-x-3 gap-y-0 border-t border-slate-100 pt-2">
            {[["preferred_name", "Preferred name"], ["native_name", "Native / local name"],
              ["romanised_name", "Romanised name"], ["department", "Department"], ["alt_email", "Alternate email"],
              ["alt_mobile", "Alternate mobile"], ["office_phone", "Office phone"], ["website", "Website"],
              ["social_links", "LinkedIn / social (comma)"], ["postal_address", "Postal address"]].map(([k, l]) => (
              <div key={k}><L>{l}</L><Input value={f[k]} onChange={(e) => set(k, e.target.value)} data-testid={`cf-${k}`} className="h-8 text-[13px]" /></div>
            ))}
          </div>
        )}

        <L>Relationship type *</L>
        <MultiChips options={meta.relationship_types} value={f.relationship_types} onChange={(v) => set("relationship_types", v)} testid="cf-relationship" />
        <L>Speciality / expertise *</L>
        <MultiChips options={SPECIALITIES} value={f.specialities} onChange={(v) => set("specialities", v)} testid="cf-speciality" allowFree />

        {/* How we met */}
        <div className="border-t border-slate-100 pt-2 grid grid-cols-3 gap-x-3 gap-y-0">
          <div><L>Date met *</L><Input type="date" value={f.date_met} onChange={(e) => set("date_met", e.target.value)} data-testid="cf-date_met" className="h-8 text-[13px]" /></div>
          <div>
            <L>Event / Trip</L>
            <select value={f.event_id} onChange={(e) => set("event_id", e.target.value)} data-testid="cf-event" className="w-full h-8 border border-slate-200 rounded-md px-2 text-[12px] bg-white">
              <option value="">—</option>
              {events.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
          <div>
            <L>Meeting type</L>
            <select value={f.meeting_type} onChange={(e) => set("meeting_type", e.target.value)} data-testid="cf-meeting_type" className="w-full h-8 border border-slate-200 rounded-md px-2 text-[12px] bg-white">
              <option value="">—</option>
              {MEETING_TYPES.map((m) => <option key={m} value={m}>{m.replace(/_/g, " ")}</option>)}
            </select>
          </div>
          {mode === "FULL" && <>
            <div><L>Venue</L><Input value={f.venue} onChange={(e) => set("venue", e.target.value)} className="h-8 text-[13px]" /></div>
            <div><L>Introduced by</L><Input value={f.introduced_by} onChange={(e) => set("introduced_by", e.target.value)} data-testid="cf-introduced_by" className="h-8 text-[13px]" /></div>
            <div><L>Who was with me</L><Input value={f.with_me} onChange={(e) => set("with_me", e.target.value)} className="h-8 text-[13px]" /></div>
          </>}
        </div>

        {/* Memory note — prominent */}
        <L>What should ONE remember about this person? *</L>
        <Textarea value={f.memory_note} onChange={(e) => set("memory_note", e.target.value)} rows={2} data-testid="cf-memory_note"
          placeholder="Record the context you'd want to remember years later." className="border-indigo-200" />

        {mode === "FULL" && (
          <div className="grid grid-cols-1 gap-1">
            {[["discussed", "What did we discuss?"], ["opportunity", "Potential opportunity"],
              ["products_interest", "Products / services of interest"], ["suntronix_help", "How can SUNTRONIX help them?"],
              ["they_help", "How can they help / collaborate?"]].map(([k, l]) => (
              <div key={k}><L>{l}</L><Input value={f[k]} onChange={(e) => set(k, e.target.value)} className="h-8 text-[13px]" /></div>
            ))}
          </div>
        )}

        {/* Follow-up */}
        <div className="border-t border-slate-100 pt-2 flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-1.5 text-[12px] text-slate-600">
            <input type="checkbox" checked={f.fu_required} onChange={(e) => set("fu_required", e.target.checked)} data-testid="cf-fu_required" /> Follow-up required
          </label>
          {f.fu_required && <>
            <Input type="date" value={f.fu_date} onChange={(e) => set("fu_date", e.target.value)} data-testid="cf-fu_date" className="h-7 w-36 text-[12px]" />
            <select value={f.fu_priority} onChange={(e) => set("fu_priority", e.target.value)} data-testid="cf-fu_priority" className="h-7 border border-slate-200 rounded-md px-2 text-[12px] bg-white">
              {["NORMAL", "IMPORTANT", "STRATEGIC"].map((p) => <option key={p}>{p}</option>)}
            </select>
            <Input value={f.fu_next_action} onChange={(e) => set("fu_next_action", e.target.value)} placeholder="Next action" className="h-7 flex-1 text-[12px]" />
          </>}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => save(true, false)} disabled={busy} data-testid="save-prepare-followup">Save & Prepare Follow-up</Button>
          <Button onClick={() => save(false, false)} disabled={busy} data-testid="save-contact" className="bg-indigo-600 hover:bg-indigo-700">Save Contact</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ── Batch Add ──────────────────────────────────────────────────── */
const BATCH_COLS = ["full_name", "honorific", "company", "designation", "email", "mobile", "city", "country", "relationship", "speciality", "memory_note"];
const BatchAdd = ({ onClose, onDone }) => {
  const [text, setText] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const parse = () => text.trim().split("\n").filter(Boolean).map((line) => {
    const cells = line.split("\t");
    const row = {}; BATCH_COLS.forEach((c, i) => { row[c] = (cells[i] || "").trim(); });
    return row;
  });
  const run = async (save) => {
    const rows = parse();
    if (!rows.length) { toast.error("Paste rows first (tab-separated, from Excel)"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/one-network/persons/batch", { rows, save });
      setResult(data.summary);
      if (save) { toast.success(`${data.summary.created} contacts created`); onDone(); }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl" data-testid="batch-dialog">
        <DialogHeader><DialogTitle className="text-base">Batch Add</DialogTitle>
          <DialogDescription className="text-[12px]">Paste tab-separated rows from Excel. Columns: {BATCH_COLS.join(" · ")}</DialogDescription></DialogHeader>
        <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={8} data-testid="batch-grid"
          placeholder="Name⇥Company⇥Designation⇥Email⇥Mobile⇥City⇥Country⇥Relationship⇥Speciality⇥Memory note" className="font-mono text-[12px]" />
        {result && (
          <div className="flex gap-2 text-[12px]" data-testid="batch-summary">
            {Object.entries(result).map(([k, v]) => <Badge key={k} className="border bg-slate-50 text-slate-600 border-slate-200">{k}: {v}</Badge>)}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => run(false)} disabled={busy} data-testid="batch-check">Check duplicates</Button>
          <Button onClick={() => run(true)} disabled={busy} data-testid="batch-save" className="bg-indigo-600 hover:bg-indigo-700">Save all valid</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ── Met Again ──────────────────────────────────────────────────── */
const MetAgainDialog = ({ personId, events, onClose, onDone }) => {
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({ date_met: today, met_city: "", met_country: "", event_id: "",
    meeting_type: "", memory_note: "", new_company: "", new_designation: "", fu_required: false });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/one-network/persons/${personId}/met-again`, {
        how_we_met: { date_met: f.date_met, city: f.met_city, country: f.met_country, event_id: f.event_id || null, meeting_type: f.meeting_type || null },
        memory_note: f.memory_note || null, new_company: f.new_company || null, new_designation: f.new_designation || null,
        follow_up: { required: f.fu_required, priority: "NORMAL" },
      });
      toast.success("Met-again encounter recorded"); onDone(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="met-again-dialog">
        <DialogHeader><DialogTitle className="text-base">+ Met Again</DialogTitle>
          <DialogDescription className="text-[12px]">Record a new meeting — same contact, new encounter.</DialogDescription></DialogHeader>
        <div className="space-y-2">
          <Input type="date" value={f.date_met} onChange={(e) => setF({ ...f, date_met: e.target.value })} data-testid="ma-date" className="h-9 text-[13px]" />
          <select value={f.event_id} onChange={(e) => setF({ ...f, event_id: e.target.value })} data-testid="ma-event" className="w-full h-9 border border-slate-200 rounded-md px-2 text-[13px] bg-white">
            <option value="">Event / trip (optional)</option>
            {events.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="City" value={f.met_city} onChange={(e) => setF({ ...f, met_city: e.target.value })} className="h-9 text-[13px]" />
            <Input placeholder="Country" value={f.met_country} onChange={(e) => setF({ ...f, met_country: e.target.value })} className="h-9 text-[13px]" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="New company (if changed)" value={f.new_company} onChange={(e) => setF({ ...f, new_company: e.target.value })} data-testid="ma-new-company" className="h-9 text-[13px]" />
            <Input placeholder="New designation" value={f.new_designation} onChange={(e) => setF({ ...f, new_designation: e.target.value })} className="h-9 text-[13px]" />
          </div>
          <Textarea placeholder="What should ONE remember?" rows={2} value={f.memory_note} onChange={(e) => setF({ ...f, memory_note: e.target.value })} data-testid="ma-note" />
        </div>
        <DialogFooter><Button onClick={save} disabled={busy} data-testid="ma-save">Save encounter</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ── Create Event ───────────────────────────────────────────────── */
const EventDialog = ({ onClose, onDone }) => {
  const [f, setF] = useState({ name: "", event_type: "EVENT", city: "", country: "", purpose: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.name.trim()) { toast.error("Event name required"); return; }
    setBusy(true);
    try { await api.post("/one-network/events", f); toast.success("Event created"); onDone(); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="event-dialog">
        <DialogHeader><DialogTitle className="text-base">New Event / Trip</DialogTitle>
          <DialogDescription className="text-[12px]">Create an event or trip to group the people you meet.</DialogDescription></DialogHeader>
        <div className="space-y-2">
          <Input placeholder="Event name (e.g. Japan Business Delegation 2026)" value={f.name}
            onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="new-event-name" className="h-9 text-[13px]" />
          <select value={f.event_type} onChange={(e) => setF({ ...f, event_type: e.target.value })}
            className="w-full h-9 border border-slate-200 rounded-md px-2 text-[13px] bg-white" data-testid="new-event-type">
            {["EVENT", "CONFERENCE", "EXHIBITION", "DELEGATION", "TRIP", "CUSTOMER_VISIT", "SOCIAL"].map((t) => <option key={t}>{t}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="City" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} className="h-9 text-[13px]" />
            <Input placeholder="Country" value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} className="h-9 text-[13px]" />
          </div>
          <Textarea placeholder="Purpose / notes" rows={2} value={f.purpose} onChange={(e) => setF({ ...f, purpose: e.target.value })} />
        </div>
        <DialogFooter><Button onClick={save} disabled={busy} data-testid="new-event-save">Create</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ── Contact 360 ────────────────────────────────────────────────── */
const Contact360 = ({ personId, onClose, onMetAgain }) => {
  const [data, setData] = useState(null);
  const [t, setT] = useState("OVERVIEW");
  useEffect(() => { api.get(`/one-network/persons/${personId}`).then((r) => setData(r.data)).catch(() => {}); }, [personId]);
  if (!data) return null;
  const p = data.person;
  const TABS = ["OVERVIEW", "AFFILIATIONS", "ENCOUNTERS", "COMMUNICATION", "FILES", "TIMELINE"];
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[86vh] overflow-y-auto" data-testid="contact-360">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">{p.full_name}
            {p.verified && <ShieldCheck className="h-4 w-4 text-emerald-500" />}
            <Badge className="text-[10px] border bg-slate-50 text-slate-600 border-slate-200 ml-auto">{p.status}</Badge>
            <Button size="sm" variant="outline" className="h-7" data-testid="c360-met-again"
              onClick={() => onMetAgain?.(p.id)}>+ Met Again</Button>
          </DialogTitle>
          <DialogDescription className="text-[12px]">
            {p.designation}{p.company ? ` · ${p.company}` : ""} · {[p.city, p.country].filter(Boolean).join(", ")}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-1">
          {(p.relationship_types || []).map((r) => <Badge key={r} className="text-[10px] border bg-indigo-50 text-indigo-700 border-indigo-200">{r.replace(/_/g, " ")}</Badge>)}
          {(p.specialities || []).map((s) => <Badge key={s} className="text-[10px] border bg-slate-50 text-slate-600 border-slate-200">{s}</Badge>)}
        </div>
        <div className="flex items-center gap-1 border-b border-slate-200 overflow-x-auto">
          {TABS.map((x) => (
            <button key={x} onClick={() => setT(x)} data-testid={`c360-tab-${x}`}
              className={`px-2.5 py-1.5 text-[12px] font-medium border-b-2 -mb-px whitespace-nowrap ${
                t === x ? "border-indigo-600 text-slate-900" : "border-transparent text-slate-500"}`}>{x}</button>
          ))}
        </div>
        <div className="text-[13px] text-slate-700 space-y-2 min-h-[120px]">
          {t === "OVERVIEW" && (
            <div className="space-y-1">
              {[["Salutation", p.salutation_display_name || (p.honorific ? `${p.honorific} ${p.surname || ""}`.trim() : null)],
                ["Honorific", p.honorific], ["Email", p.email], ["Mobile", p.mobile], ["Website", p.website],
                ["Last met", p.last_interaction], ["Visibility", p.visibility]].map(([k, v]) => (
                <div key={k} className="flex gap-2"><span className="w-24 text-slate-400 text-[12px]">{k}</span><span>{v || "—"}</span></div>
              ))}
              {(p.memory_notes || []).map((n, i) => <div key={i} className="text-[12px] bg-amber-50 border border-amber-100 rounded px-2 py-1 mt-1">💡 {n.note}</div>)}
            </div>
          )}
          {t === "AFFILIATIONS" && data.affiliations.map((a) => (
            <div key={a.id} className="flex items-center gap-2 border border-slate-100 rounded px-2 py-1.5">
              <Building2 className="h-4 w-4 text-slate-400" />
              <span className="flex-1">{a.designation} · {a.company}</span>
              <Badge className={`text-[10px] border ${a.current ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-500 border-slate-200"}`}>{a.current ? "Current" : `Former (to ${a.to_date || "—"})`}</Badge>
            </div>
          ))}
          {t === "FILES" && data.cards.map((c) => (
            <div key={c.id} className="flex items-center gap-2 border border-slate-100 rounded px-2 py-1.5">
              <img src={`${api.defaults.baseURL}/one-network/cards/${c.id}/image`} alt="card" className="h-12 rounded border" onError={(e) => { e.target.style.display = "none"; }} />
              <span className="text-[12px] text-slate-500">{c.source} · {(c.created_at || "").slice(0, 10)}</span>
            </div>
          ))}
          {t === "ENCOUNTERS" && data.encounters.map((e) => (
            <div key={e.id} className="border border-slate-100 rounded px-2 py-1.5">
              <div className="text-[13px]">{e.event_name || e.meeting_type} · {e.date}</div>
              {e.notes && <div className="text-[12px] text-slate-500">{e.notes}</div>}
            </div>
          ))}
          {t === "COMMUNICATION" && data.communications.map((c) => (
            <div key={c.id} className="border border-slate-100 rounded px-2 py-1.5">
              <div className="flex items-center justify-between"><span className="text-[13px] font-medium">{c.subject}</span>
                <Badge className="text-[10px] border bg-slate-50 text-slate-600 border-slate-200">{c.status}</Badge></div>
              <div className="text-[12px] text-slate-500 line-clamp-2">{c.body}</div>
            </div>
          ))}
          {t === "TIMELINE" && data.timeline.map((tl, i) => (
            <div key={i} className="flex items-center gap-2 text-[12px]"><ChevronRight className="h-3 w-3 text-slate-300" />
              <span className="text-slate-400 w-20">{(tl.at || "").slice(0, 10)}</span><span>{tl.label}</span></div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ── Quick Email · Contact Email Picker (one-off follow-up) ─────── */
const QuickEmailDialog = ({ meta, onClose }) => {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [picked, setPicked] = useState(null);
  const [libTpls, setLibTpls] = useState([]);
  const [tplId, setTplId] = useState("");
  const [level, setLevel] = useState("WARM");
  const [ai, setAi] = useState(false);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);
  const [attachProfile, setAttachProfile] = useState(false);

  const search = useCallback(() => {
    api.get(`/one-network/contacts/pickable?q=${encodeURIComponent(q)}`).then((r) => setItems(r.data.items || [])).catch(() => {});
  }, [q]);
  useEffect(() => { const t = setTimeout(search, 250); return () => clearTimeout(t); }, [search]);
  useEffect(() => {
    api.get("/one-network/templates?tab=ALL").then((r) => {
      const its = r.data.items || [];
      setLibTpls(its);
      if (its.length) setTplId(its.find((t) => t.category === "BUSINESS")?.id || its[0].id);
    }).catch(() => {});
  }, []);

  const prepare = async () => {
    if (!picked) return;
    setBusy(true);
    try {
      const { data } = await api.post("/one-network/comms/prepare", { person_id: picked.id, template_id: tplId, level, ai });
      setDraft(data.draft);
      toast.success("Draft prepared");
    } catch (e) {
      const det = e.response?.data?.detail;
      toast.error(typeof det === "string" ? det : "Could not prepare draft");
    }
    setBusy(false);
  };
  const sendDraft = async (mode) => {
    try {
      const { data } = await api.post(`/one-network/comms/${draft.id}/send`, { mode, attach_profile: attachProfile });
      const st = data.status === "SENT" || data.status === "ALREADY_SENT" ? "Sent via Outlook" : "Outlook draft created";
      toast.success(st);
      setDraft((d) => ({ ...d, status: data.status === "SENT" || data.status === "ALREADY_SENT" ? "SENT" : "DRAFT_CREATED" }));
    } catch (e) {
      const det = e.response?.data?.detail;
      toast.error(typeof det === "string" ? det : "Outlook sending is not configured yet");
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-xl max-h-[86vh] overflow-y-auto" data-testid="quick-email-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2"><Mail className="h-4 w-4 text-indigo-600" /> Quick follow-up email</DialogTitle>
          <DialogDescription className="text-[12px]">Pick any verified contact with an email and send a one-off follow-up.</DialogDescription>
        </DialogHeader>

        {!draft && (
          <div className="space-y-3">
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-2 top-2.5 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, company or email…" className="h-8 pl-7 text-[13px]" data-testid="quick-email-search" />
            </div>
            <div className="max-h-52 overflow-y-auto space-y-1" data-testid="quick-email-results">
              {items.map((c) => (
                <button key={c.id} onClick={() => setPicked(c)} data-testid={`quick-email-pick-${c.id}`}
                  className={`w-full text-left rounded-lg border p-2 transition-colors ${picked?.id === c.id ? "border-indigo-400 bg-indigo-50" : "border-slate-200 hover:bg-slate-50"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-[13px] font-medium text-slate-900 truncate">{c.full_name} <span className="text-slate-400 font-normal">· {c.email}</span></div>
                      <div className="text-[11px] text-slate-500 truncate">{[c.designation, c.company].filter(Boolean).join(" · ")}</div>
                    </div>
                    {!c.email_ready && <Badge className="text-[9px] border bg-amber-50 text-amber-700 border-amber-200 shrink-0">Salutation needed</Badge>}
                  </div>
                </button>
              ))}
              {items.length === 0 && <div className="text-[12px] text-slate-400 py-6 text-center">No verified contacts with an email match.</div>}
            </div>

            {picked && (
              <div className="rounded-lg border border-slate-200 p-3 space-y-2">
                <div className="text-[12px] text-slate-600">To: <b>{picked.full_name}</b> · {picked.email}</div>
                {!picked.email_ready ? (
                  <div className="text-[12px] rounded bg-amber-50 border border-amber-200 text-amber-800 px-2 py-1.5" data-testid="quick-email-salutation-warning">
                    This contact needs an honorific/salutation before a follow-up can be drafted. Open the contact to set one.
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      <select value={tplId} onChange={(e) => setTplId(e.target.value)} data-testid="quick-email-template"
                        className="h-9 border border-slate-200 rounded-md px-2 text-[13px] bg-white flex-1">
                        {libTpls.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                      </select>
                      <select value={level} onChange={(e) => setLevel(e.target.value)} data-testid="quick-email-level"
                        className="h-9 border border-slate-200 rounded-md px-2 text-[13px] bg-white">
                        {(meta.levels || ["WARM"]).map((l) => <option key={l} value={l}>{l.replace(/_/g, " ")}</option>)}
                      </select>
                    </div>
                    <div className="flex items-center justify-between">
                      <label className="text-[12px] text-slate-600 flex items-center gap-1.5">
                        <input type="checkbox" checked={ai} onChange={(e) => setAi(e.target.checked)} data-testid="quick-email-ai" /> AI personalise
                      </label>
                      <Button onClick={prepare} disabled={busy || !tplId} data-testid="quick-email-prepare" className="bg-indigo-600 hover:bg-indigo-700">
                        <Sparkles className="h-4 w-4 mr-1.5" /> Prepare draft
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {draft && (
          <div className="space-y-2" data-testid="quick-email-draft">
            <div className="flex items-center justify-between">
              <div className="text-[13px] font-medium">{draft.person_name} <span className="text-slate-400 font-normal">· {draft.to_email}</span></div>
              <Badge className="text-[10px] border bg-slate-50 text-slate-600 border-slate-200" data-testid="quick-email-status">{draft.status}</Badge>
            </div>
            <div className="text-[12px] text-slate-600"><b>{draft.subject}</b></div>
            <div className="text-[12px] text-slate-500 whitespace-pre-wrap max-h-56 overflow-y-auto border border-slate-100 rounded p-2">{draft.body}</div>
            <label className="flex items-center gap-1.5 text-[12px] text-slate-600">
              <input type="checkbox" checked={attachProfile} onChange={(e) => setAttachProfile(e.target.checked)} data-testid="quick-email-attach-profile" />
              Attach SUNTRONIX company profile PDF
            </label>
            <div className="flex gap-1.5">
              <Button size="sm" variant="outline" onClick={() => setDraft(null)} data-testid="quick-email-back">Back</Button>
              <Button size="sm" variant="outline" onClick={() => sendDraft("DRAFT")} data-testid="quick-email-outlook-draft">Create Outlook draft</Button>
              <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => sendDraft("SEND")} data-testid="quick-email-send"><Send className="h-3.5 w-3.5 mr-1" /> Send via Outlook</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};


/* ── Event Detail + completion dashboard ────────────────────────── */
const EventDetail = ({ eventId, meta, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [libTpls, setLibTpls] = useState([]);
  const [tplId, setTplId] = useState("");
  const [level, setLevel] = useState("WARM");
  const [ai, setAi] = useState(false);
  const [busy, setBusy] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [attachProfile, setAttachProfile] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const load = useCallback(() => { api.get(`/one-network/events/${eventId}`).then((r) => setData(r.data)).catch(() => {}); }, [eventId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/one-network/templates?tab=ALL").then((r) => {
      const items = r.data.items || [];
      setLibTpls(items);
      if (items.length) setTplId(items.find((t) => t.category === "BUSINESS")?.id || items[0].id);
    }).catch(() => {});
  }, []);
  if (!data) return null;
  const d = data.dashboard;
  const prepare = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/one-network/events/${eventId}/prepare-followups`, { template_id: tplId, level, ai });
      setDrafts(res.drafts || []);
      toast.success(`${res.count} individual draft(s) prepared`);
      load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  const sendDraft = async (cid, mode) => {
    try {
      const { data } = await api.post(`/one-network/comms/${cid}/send`, { mode, attach_profile: attachProfile });
      const st = data.status === "SENT" || data.status === "ALREADY_SENT" ? "Sent via Outlook"
        : "Outlook draft created";
      toast.success(st);
      setDrafts((prev) => prev.map((d) => d.id === cid
        ? { ...d, status: data.status === "SENT" || data.status === "ALREADY_SENT" ? "SENT" : "DRAFT_CREATED" } : d));
    } catch (e) {
      const det = e.response?.data?.detail;
      toast.error(typeof det === "string" ? det : "Outlook sending is not configured yet");
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[86vh] overflow-y-auto" data-testid="event-detail">
        <DialogHeader>
          <DialogTitle className="text-base">{data.event.name}</DialogTitle>
          <DialogDescription className="text-[12px]">{[data.event.city, data.event.country].filter(Boolean).join(", ")} · {data.event.event_type}</DialogDescription>
        </DialogHeader>
        <div className="flex justify-end -mt-1">
          <Button size="sm" variant="outline" data-testid="event-import-attendees"
            onClick={() => setImportOpen(true)}>
            <UploadCloud className="h-3.5 w-3.5 mr-1.5" /> Import attendee list
          </Button>
        </div>
        <div className="grid grid-cols-3 md:grid-cols-4 gap-2" data-testid="event-dashboard">
          {[["Cards", d.cards_captured], ["Verified", d.contacts_verified], ["Email ✓", d.email_available],
            ["Email ✗", d.email_missing], ["Salut. ✗", d.salutation_missing], ["Drafts", d.followups_ready],
            ["Sent", d.sent], ["Replied", d.replied], ["Pending", d.follow_up_pending]].map(([k, v]) => (
            <div key={k} className="rounded border border-slate-200 bg-white px-2 py-1.5">
              <div className="text-[10px] uppercase text-slate-400">{k}</div>
              <div className={`text-base font-semibold ${(k === "Pending" || k === "Salut. ✗") && v > 0 ? "text-amber-600" : "text-slate-900"}`}>{v}</div>
            </div>
          ))}
        </div>
        <div className={`text-[12px] rounded px-3 py-2 border ${d.can_complete ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-800"}`} data-testid="event-completion">
          {d.can_complete ? "All eligible follow-ups handled — event complete." : `Follow-up pending = ${d.follow_up_pending}. Event cannot be marked complete.`}
        </div>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <select value={tplId} onChange={(e) => setTplId(e.target.value)} data-testid="followup-template"
              className="h-9 border border-slate-200 rounded-md px-2 text-[13px] bg-white flex-1">
              {libTpls.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <select value={level} onChange={(e) => setLevel(e.target.value)} data-testid="followup-level"
              className="h-9 border border-slate-200 rounded-md px-2 text-[13px] bg-white">
              {(meta.levels || ["WARM"]).map((l) => <option key={l} value={l}>{l.replace(/_/g, " ")}</option>)}
            </select>
          </div>
          <div className="flex items-center justify-between">
            <label className="text-[12px] text-slate-600 flex items-center gap-1.5">
              <input type="checkbox" checked={ai} onChange={(e) => setAi(e.target.checked)} data-testid="followup-ai" />
              AI personalise each draft
            </label>
            <Button onClick={prepare} disabled={busy || !tplId} data-testid="prepare-followups" className="bg-indigo-600 hover:bg-indigo-700">
              <Sparkles className="h-4 w-4 mr-1.5" /> Prepare all follow-ups
            </Button>
          </div>
        </div>
        {drafts.length > 0 && (
          <div className="space-y-2" data-testid="followup-drafts">
            <label className="flex items-center gap-1.5 text-[12px] text-slate-600">
              <input type="checkbox" checked={attachProfile} onChange={(e) => setAttachProfile(e.target.checked)} data-testid="attach-profile-toggle" />
              Attach SUNTRONIX company profile PDF to each email
            </label>
            {drafts.map((dr) => (
              <div key={dr.id} className="border border-slate-200 rounded-lg p-2">
                <div className="flex items-center justify-between">
                  <div className="text-[13px] font-medium">{dr.person_name} <span className="text-slate-400 font-normal">· {dr.to_email}</span></div>
                  <Badge className="text-[10px] border bg-slate-50 text-slate-600 border-slate-200" data-testid={`draft-status-${dr.id}`}>{dr.status}</Badge>
                </div>
                <div className="text-[12px] text-slate-600 mt-1"><b>{dr.subject}</b></div>
                <div className="text-[12px] text-slate-500 whitespace-pre-wrap line-clamp-4 mt-0.5">{dr.body}</div>
                <div className="flex gap-1.5 mt-2">
                  <Button size="sm" variant="outline" data-testid={`draft-outlook-${dr.id}`}
                    onClick={() => sendDraft(dr.id, "DRAFT")}>Create Outlook draft</Button>
                  <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" data-testid={`draft-send-${dr.id}`}
                    onClick={() => sendDraft(dr.id, "SEND")}><Send className="h-3.5 w-3.5 mr-1" /> Send via Outlook</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
      {importOpen && (
        <AttendeeImport eventId={eventId} eventName={data.event.name}
          onClose={() => setImportOpen(false)}
          onDone={() => { load(); onChanged?.(); }} />
      )}
    </Dialog>
  );
};

/* ── Attendee / Delegate list import ──────────────────────────────── */
const AttendeeImport = ({ eventId, eventName, onClose, onDone }) => {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [done, setDone] = useState(null);

  const run = async (save) => {
    if (!file) { toast.error("Choose a CSV or XLSX file"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/one-network/events/${eventId}/attendees/import?save=${save}`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      if (save) {
        setDone(data);
        toast.success(`${data.summary.created} added · ${data.summary.reconciled} reconciled`);
        onDone?.();
      } else {
        setPreview(data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    }
    setBusy(false);
  };

  const s = preview?.summary || done?.summary;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="attendee-import-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <FileSpreadsheet className="h-4 w-4 text-indigo-600" /> Import attendee list
          </DialogTitle>
          <DialogDescription className="text-[12px]">
            Upload the delegate list (.csv or .xlsx) for <b>{eventName}</b>. We match against your
            existing contacts, add new ones, and link everyone to this event.
          </DialogDescription>
        </DialogHeader>

        {!done && (
          <div className="space-y-3">
            <label className="block border-2 border-dashed border-slate-200 rounded-lg p-4 text-center cursor-pointer hover:border-indigo-300"
              data-testid="attendee-file-drop">
              <UploadCloud className="h-6 w-6 mx-auto text-slate-400" />
              <div className="text-[13px] text-slate-700 mt-1">{file ? file.name : "Click to choose a CSV / XLSX file"}</div>
              <div className="text-[11px] text-slate-400 mt-0.5">Columns like Name, Company, Email, Mobile, Designation are auto-detected</div>
              <input type="file" accept=".csv,.xlsx,.xlsm,.txt" className="hidden"
                data-testid="attendee-file-input"
                onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }} />
            </label>

            {preview && (
              <div className="space-y-2" data-testid="attendee-preview">
                <div className="text-[11px] text-slate-500">
                  Detected columns: {(preview.detected_columns || []).slice(0, 8).join(", ") || "—"}
                </div>
                <div className="grid grid-cols-4 gap-2 text-center">
                  {[["New", s.new, "text-emerald-700"], ["Matched", s.matched, "text-sky-700"],
                    ["Review", s.review_required, "text-amber-700"], ["Invalid", s.invalid, "text-rose-700"]].map(([k, v, c]) => (
                    <div key={k} className="rounded border border-slate-200 bg-white py-1.5">
                      <div className="text-[10px] uppercase text-slate-400">{k}</div>
                      <div className={`text-lg font-semibold ${c}`} data-testid={`attendee-prev-${k.toLowerCase()}`}>{v}</div>
                    </div>
                  ))}
                </div>
                <div className="text-[11px] text-slate-500">
                  {preview.row_count} rows read. Matched contacts will be linked to this event; new ones will be created as PRIVATE contacts.
                </div>
              </div>
            )}
          </div>
        )}

        {done && (
          <div className="space-y-3 text-center py-2" data-testid="attendee-import-done">
            <CheckCircle2 className="h-9 w-9 mx-auto text-emerald-600" />
            <div className="text-[14px] font-medium text-slate-900">Import complete</div>
            <div className="grid grid-cols-3 gap-2 text-center">
              {[["Added", done.summary.created], ["Reconciled", done.summary.reconciled], ["Review", done.summary.review_required]].map(([k, v]) => (
                <div key={k} className="rounded border border-slate-200 bg-white py-1.5">
                  <div className="text-[10px] uppercase text-slate-400">{k}</div>
                  <div className="text-lg font-semibold text-slate-900">{v}</div>
                </div>
              ))}
            </div>
            {done.summary.review_required > 0 && (
              <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                {done.summary.review_required} row(s) need manual review (possible duplicate) — resolve them in your network.
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {done ? (
            <Button onClick={onClose} data-testid="attendee-close" className="bg-indigo-600 hover:bg-indigo-700">Done</Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => run(false)} disabled={busy || !file} data-testid="attendee-preview-btn">
                {busy && !preview ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} Preview
              </Button>
              <Button onClick={() => run(true)} disabled={busy || !file} data-testid="attendee-import-btn"
                className="bg-indigo-600 hover:bg-indigo-700">
                {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} Import &amp; reconcile
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
